import { Clock, Award } from 'lucide-react';

// Deterministic hash from string for consistent pattern selection
const hashStr = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

// Artistic SVG patterns — clean geometric lines
const patterns = [
  // Concentric arcs
  (w, h) => (
    <g opacity="0.35">
      {[0.3, 0.5, 0.7, 0.9].map((r, i) => (
        <circle key={i} cx={w * 0.2} cy={h * 1.1} r={Math.min(w, h) * r} fill="none" stroke="white" strokeWidth="1.5" />
      ))}
      <line x1={w * 0.6} y1={0} x2={w} y2={h * 0.4} stroke="white" strokeWidth="1" />
      <line x1={w * 0.7} y1={0} x2={w} y2={h * 0.55} stroke="white" strokeWidth="0.8" />
    </g>
  ),
  // Diagonal grid
  (w, h) => (
    <g opacity="0.3">
      {[0.15, 0.35, 0.55, 0.75, 0.95].map((p, i) => (
        <line key={`a${i}`} x1={w * p} y1={0} x2={0} y2={h * p} stroke="white" strokeWidth="1.2" />
      ))}
      {[0.2, 0.5, 0.8].map((p, i) => (
        <line key={`b${i}`} x1={w * p} y1={h} x2={w} y2={h * (1 - p)} stroke="white" strokeWidth="0.8" />
      ))}
    </g>
  ),
  // Stacked chevrons
  (w, h) => (
    <g opacity="0.3">
      {[0.25, 0.45, 0.65, 0.85].map((y, i) => (
        <polyline key={i} points={`${w * 0.1},${h * y} ${w * 0.5},${h * (y - 0.12)} ${w * 0.9},${h * y}`} fill="none" stroke="white" strokeWidth="1.5" strokeLinejoin="round" />
      ))}
    </g>
  ),
  // Radial burst
  (w, h) => (
    <g opacity="0.25">
      {Array.from({ length: 8 }, (_, i) => {
        const angle = (i * Math.PI) / 4;
        return (
          <line key={i} x1={w * 0.75} y1={h * 0.5} x2={w * 0.75 + Math.cos(angle) * w * 0.5} y2={h * 0.5 + Math.sin(angle) * h * 0.6} stroke="white" strokeWidth="1.2" />
        );
      })}
      <circle cx={w * 0.75} cy={h * 0.5} r={8} fill="none" stroke="white" strokeWidth="1.5" />
      <circle cx={w * 0.75} cy={h * 0.5} r={20} fill="none" stroke="white" strokeWidth="0.8" />
    </g>
  ),
  // Flowing curves
  (w, h) => (
    <g opacity="0.3">
      <path d={`M0,${h * 0.3} Q${w * 0.3},${h * 0.1} ${w * 0.6},${h * 0.35} T${w},${h * 0.2}`} fill="none" stroke="white" strokeWidth="1.5" />
      <path d={`M0,${h * 0.55} Q${w * 0.35},${h * 0.35} ${w * 0.65},${h * 0.6} T${w},${h * 0.45}`} fill="none" stroke="white" strokeWidth="1.2" />
      <path d={`M0,${h * 0.8} Q${w * 0.25},${h * 0.65} ${w * 0.55},${h * 0.85} T${w},${h * 0.7}`} fill="none" stroke="white" strokeWidth="0.8" />
    </g>
  ),
  // Hexagonal mesh
  (w, h) => (
    <g opacity="0.25">
      {[
        [0.2, 0.3], [0.5, 0.15], [0.8, 0.3], [0.35, 0.6], [0.65, 0.6], [0.5, 0.85]
      ].map(([cx, cy], i) => (
        <polygon key={i} points={Array.from({ length: 6 }, (_, j) => {
          const a = (j * Math.PI) / 3 - Math.PI / 6;
          return `${w * cx + 18 * Math.cos(a)},${h * cy + 18 * Math.sin(a)}`;
        }).join(' ')} fill="none" stroke="white" strokeWidth="1.2" />
      ))}
      <line x1={w * 0.2} y1={h * 0.3} x2={w * 0.5} y2={h * 0.15} stroke="white" strokeWidth="0.7" />
      <line x1={w * 0.5} y1={h * 0.15} x2={w * 0.8} y2={h * 0.3} stroke="white" strokeWidth="0.7" />
      <line x1={w * 0.35} y1={h * 0.6} x2={w * 0.65} y2={h * 0.6} stroke="white" strokeWidth="0.7" />
    </g>
  ),
];

// Single brand-blue gradient — used by every topic card so the palette
// stays on a strict 2-tone (brand blue + cyan accent) system.
const BASE_GRADIENTS = [
  ['#0d3a71', '#1a6db5'],
];

// Card-sized thumbnail (for Landing page)
export function TopicThumbnailCard({ topic, isComingSoon = false }) {
  const h = hashStr(topic.topic_id || topic.name || '');
  const grad = BASE_GRADIENTS[h % BASE_GRADIENTS.length];
  const pattern = patterns[h % patterns.length];

  if (topic.image_url) {
    return (
      <div className="relative h-[72px] overflow-hidden">
        <img src={topic.image_url} alt={topic.name} width={400} height={72} loading="lazy" className="w-full h-full object-cover" />
        <Badges topic={topic} isComingSoon={isComingSoon} />
      </div>
    );
  }

  // Artistic generated graphic
  return (
    <div className={`relative h-[72px] overflow-hidden ${isComingSoon ? 'opacity-60' : ''}`}>
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 80" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id={`tg-${topic.topic_id}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={grad[0]} />
            <stop offset="100%" stopColor={grad[1]} />
          </linearGradient>
        </defs>
        <rect width="400" height="80" fill={`url(#tg-${topic.topic_id})`} />
        {pattern(400, 80)}
      </svg>
      <Badges topic={topic} isComingSoon={isComingSoon} />
    </div>
  );
}

// Inline thumbnail (for Dashboard rows)
export function TopicThumbnailInline({ topic }) {
  const h = hashStr(topic.topic_id || topic.name || '');
  const grad = BASE_GRADIENTS[h % BASE_GRADIENTS.length];
  const pat = patterns[h % patterns.length];

  if (topic.image_url) {
    return (
      <div className="relative w-11 h-11 rounded-lg overflow-hidden flex-shrink-0">
        <img src={topic.image_url} alt="" width={44} height={44} loading="lazy" className="w-full h-full object-cover" />
      </div>
    );
  }

  return (
    <div className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0">
      <svg className="w-full h-full" viewBox="0 0 44 44">
        <defs>
          <linearGradient id={`ti-${topic.topic_id}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={grad[0]} />
            <stop offset="100%" stopColor={grad[1]} />
          </linearGradient>
        </defs>
        <rect width="44" height="44" fill={`url(#ti-${topic.topic_id})`} />
        {pat(44, 44)}
      </svg>
    </div>
  );
}

// Shared badge overlay
function Badges({ topic, isComingSoon }) {
  return (
    <div className="absolute top-2.5 right-2.5 flex items-center gap-1.5 z-10">
      {isComingSoon && (
        <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-black/40 text-white backdrop-blur-sm">
          <Clock className="w-3 h-3" /> Coming Soon
        </div>
      )}
      {!isComingSoon && topic.has_certificate !== false && (
        <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-white/15 text-white border border-white/30 backdrop-blur-sm">
          <Award className="w-3 h-3" /> Certificate
        </div>
      )}
    </div>
  );
}

